import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../hooks/useToast';
import { useUserStore } from '../stores/useUserStore';
import passwordVerificationService from '../services/passwordVerificationService';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

interface ReAuthenticationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
  description?: string;
}

export const ReAuthenticationDialog: React.FC<ReAuthenticationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  title,
  description,
}) => {
  const { translate } = useTranslation();
  const { success, error: showError } = useToast();
  const { user } = useUserStore();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!password) {
        setError(
          translate(
            'auth.password_required',
            'Password is required for re-authentication'
          )
        );
        setLoading(false);
        return;
      }

      if (!user) {
        setError(
          translate('auth.user_not_found', 'User information not found')
        );
        setLoading(false);
        return;
      }

      // Get username or email from user
      const usernameOrEmail =
        user.username || user.email || (user as any).name || '';

      if (!usernameOrEmail) {
        setError(
          translate(
            'auth.username_email_not_found',
            'Username or email not found'
          )
        );
        setLoading(false);
        return;
      }

      // Verify password using the verification service
      const isValid = await passwordVerificationService.verifyPassword(
        password
      );

      if (isValid) {
        success(
          translate(
            'auth.reauthentication_success',
            'Re-authentication successful'
          )
        );
        setPassword('');
        onSuccess();
        onClose();
      } else {
        setError(
          translate(
            'auth.invalid_password',
            'Invalid password. Please try again.'
          )
        );
      }
    } catch (err: any) {
      setError(
        err?.message ||
          translate(
            'auth.reauthentication_error',
            'An error occurred during re-authentication'
          )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPassword('');
      setError(null);
      setShowPassword(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            <DialogTitle>
              {title ||
                translate(
                  'auth.reauthentication_required',
                  'Re-authentication Required'
                )}
            </DialogTitle>
          </div>
          <DialogDescription>
            {description ||
              translate(
                'auth.reauthentication_description',
                'Please enter your password to reveal sensitive data. This is required for security purposes.'
              )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">
                {translate('auth.password', 'Password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={translate(
                    'auth.enter_password',
                    'Enter your password'
                  )}
                  disabled={loading}
                  autoFocus
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading || !password}>
              {loading
                ? translate('auth.verifying', 'Verifying...')
                : translate('auth.verify', 'Verify')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
